import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { MethodTypeEditorEffects } from '../../../effects/method-type-editor.effects';
import type { MethodTypeEditorContext } from '../../../effects/method-type-editor.effects';
import { SaveProtocolCascadeEffects } from '../../../effects/save-protocol-cascade.effects';
import { MethodTypeEditorState } from '../../../state/method-type-editor.state';

/**
 * The editing session's identity, provided ONCE at page scope: the route's
 * method-type id for an existing method type, or a minted `tmp-<uuid>`
 * placeholder for a new one. Every per-mount consumer token derives from it
 * (`METHOD_PIPELINE_MANAGER_CONTEXT`, `RESOURCE_MANAGER_CONTAINER_ID`, the
 * resource-tree root), and the future `consolidateMethodType` remaps the
 * placeholder to the persisted id through the cascade's method-type map.
 */
export const METHOD_TYPE_EDITOR_CONTEXT = new InjectionToken<MethodTypeEditorContext>('METHOD_TYPE_EDITOR_CONTEXT');

/**
 * Save-cascade consumer for the route host (`MethodTypeEditorPageComponent`).
 *
 * `MethodTypeEditorComponent` is a pure draft producer — its `save`/`cancel`
 * emit a one-shot `MethodTypeEditorState.outcome$` event and never persist.
 * This facade owns the route-side decision: on `saved` it triggers the
 * method-type consolidation (TODAY A STUB that completes immediately and
 * persists nothing — the persistence work package replaces its body; this
 * wiring is final), and it exposes the navigation intents (`saved$`,
 * `cancelled$`) for the thin host to react to.
 */
@Injectable()
export class MethodTypeEditorPageFacade {
  private readonly context = inject<MethodTypeEditorContext>(METHOD_TYPE_EDITOR_CONTEXT);

  constructor(
    private readonly editorEffects: MethodTypeEditorEffects,
    private readonly state: MethodTypeEditorState,
    private readonly cascadeEffects: SaveProtocolCascadeEffects
  ) {}

  /**
   * Loads the editor for the session's method type (the effects also seed the
   * shared assigned-resources tree) and wires the save reaction: every `saved`
   * outcome triggers the cascade. Built on `state.effect` so the ongoing
   * subscription's teardown follows the per-page state lifecycle, and a retry
   * after a failure re-triggers naturally.
   */
  public readonly initialize = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => this.editorEffects.loadMethodType(this.context)),
      switchMap(() => this.state.outcome$),
      filter((outcome) => outcome.kind === 'saved'),
      tap(() => this.cascadeEffects.consolidateMethodType())
    )
  );

  /** Navigation intent: the method-type consolidation finished. */
  public readonly saved$: Observable<void> = this.cascadeEffects.cascadeCompleted$;

  /** Navigation intent: the user discarded the edit. */
  public readonly cancelled$: Observable<void> = this.state.outcome$.pipe(
    filter((outcome) => outcome.kind === 'cancelled'),
    map(() => undefined)
  );
}
